<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Select, SliderV2, Text } from '@Shelly/shelly-design-system';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import type { NightVisionMode, NightVisionSensitivity } from '../../types/camera-settings';

	export let device: Device;

	const SENSITIVITY_VALUES: readonly NightVisionSensitivity[] = ['low', 'medium', 'high'] as const;
	const SENSITIVITY_INDEX: Record<NightVisionSensitivity, number> = {
		low: 0,
		medium: 1,
		high: 2,
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const readNightVision = (): { mode: NightVisionMode; sensitivity: NightVisionSensitivity } => {
		const nv = getCameraConfig(device)?.night_vision;
		const auto = nv?.auto ?? true;
		const ir_leds = nv?.ir_leds ?? true;
		const sensitivity: NightVisionSensitivity = nv?.sensitivity ?? 'medium';

		let mode: NightVisionMode = 'disabled';
		if (auto && ir_leds) mode = 'enabled';
		else if (auto && !ir_leds) mode = 'bw';

		return { mode, sensitivity };
	};

	const modeToConfig = (mode: NightVisionMode) => {
		switch (mode) {
			case 'enabled':
				return { auto: true, ir_leds: true };
			case 'bw':
				return { auto: true, ir_leds: false };
			case 'disabled':
				return { auto: false, ir_leds: false };
		}
	};

	let { mode, sensitivity } = readNightVision();
	let cacheMode = mode;
	let cacheSensitivity = sensitivity;
	let loading = false;

	const AUTOSAVE_DELAY = 500;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: modeOptions = [
		{ title: $format('camera_nv_enabled', { default: 'Enabled' }), value: 'enabled' },
		{ title: $format('camera_nv_bw', { default: 'Black & White' }), value: 'bw' },
		{ title: $format('camera_nv_disabled', { default: 'Disabled' }), value: 'disabled' },
	];

	$: sensitivityIndex = SENSITIVITY_INDEX[sensitivity] ?? 1;

	$: hasChanges = mode !== cacheMode || sensitivity !== cacheSensitivity;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const getSelectValue = (e: CustomEvent): any => {
		const d = e.detail;
		if (typeof d === 'string' || typeof d === 'number') return d;
		return d?.value ?? d;
	};

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ night_vision: { ...modeToConfig(mode), sensitivity } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cacheMode = mode;
			cacheSensitivity = sensitivity;
		} else {
			mode = cacheMode;
			sensitivity = cacheSensitivity;
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const success = await saveCameraConfig(
			device,
			{ night_vision: null },
			$format('camera_nv_reset_success', { default: 'Night vision settings reset to defaults.' }),
		);
		if (success) {
			({ mode, sensitivity } = readNightVision());
			cacheMode = mode;
			cacheSensitivity = sensitivity;
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

<Text styleType="h2" class="mb-s">
	{$format('label_night_vision', { default: 'Night vision' })}
</Text>

<Select
	size="m"
	width="full"
	icon="fas fa-moon"
	label={$format('camera_nv_mode', { default: 'Auto night vision' })}
	options={modeOptions}
	value={modeOptions.find((o) => o.value === mode)}
	on:change={(e) => {
		mode = getSelectValue(e);
		scheduleSave();
	}}
	disabled={loading}
	id="camera-nv-mode"
	aria-label={$format('camera_nv_mode', { default: 'Auto night vision' })}
/>

<SliderV2
	id="camera-nv-sensitivity"
	class="mt-m"
	value={sensitivityIndex}
	on:value_change={(e) => {
		sensitivity = SENSITIVITY_VALUES[e.detail.value] ?? 'medium';
		scheduleSave();
	}}
	disabled={loading}
	min={0}
	max={2}
	step={1}
	type="incremented"
	title={$format('camera_nv_sensitivity', { default: 'Sensitivity' })}
	titleTypography="body-small"
	unit=""
	a11yProps={{ ariaLabel: 'Sensitivity', ariaLabelValue: 'Sensitivity value' }}
	lineMarkers={[
		{ value: 0, displayValue: $format('camera_nv_low', { default: 'Low' }), showLabel: true },
		{ value: 1, displayValue: $format('camera_nv_medium', { default: 'Medium' }), showLabel: true },
		{ value: 2, displayValue: $format('camera_nv_high', { default: 'High' }), showLabel: true },
	]}
/>

{#if !$showNewLayout}
	<Button
		class="mt-m"
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
